import { For, Match, ParentProps, Show, Switch, createContext, createEffect, createMemo, createSignal, onMount } from 'solid-js'
import { requireContext, useDropdownContext } from 'common'
import { AgentConversation, AgentKnowledgeBaseReference, AgentMessage, AgentSenderType, UserRating } from 'st-app/api'
import { SuppportAssistAgentContext } from './SupportAssistAgentContext'
import styles from './st-support-assist-agent.module.css'
import Button from '../st-button/st-button'
import Flexbox from '../st-flexbox/st-flexbox'
import Markdown from '../st-markdown/st-markdown'
import Dropdown from '../st-dropdown/st-dropdown'
import Spinner from '../st-spinner/st-spinner'
import Loader from '../st-loader/st-loader'

const SuppportAssistAgentContextJsx = createContext<SuppportAssistAgentContext>();
export const requireSuppportAssistAgentContext = () => {
    return requireContext(SuppportAssistAgentContextJsx);
}

export class SuppportAssistAgentProps { }

export default function SuppportAssistAgent(props: ParentProps<SuppportAssistAgentProps>) {
    const context = new SuppportAssistAgentContext();

    return (
        <SuppportAssistAgentContextJsx.Provider value={context}>
            <SuppportAssistAgentInternal {...props} />
        </SuppportAssistAgentContextJsx.Provider>
    )
}

function SuppportAssistAgentInternal(props: ParentProps<SuppportAssistAgentProps>) {
    const context = requireSuppportAssistAgentContext();
    const [collapsed, setCollapsed] = createSignal(false);

    onMount(() => {
        context.loadConversations()
        .then(() => {
            context.setConversationGu('6818362d-a0e1-4223-b7d0-5d10aaace1f8')
        });
    })

    const references = createMemo(() => context.messages.flatMap(m => m.traces).flatMap(t => t.orchestration?.knowledgeBaseOutput?.references ?? []));

    return (
        <div class={styles['st-support-assist-agent']} data-collapsed={collapsed()}>
            <Flexbox dir='vertical' class={styles['conversation-list']}>
                <Flexbox dir='horizontal' class={styles['conversation-list-header']}>
                    <Button class={styles['conversation-list-item']} icon='fa-regular fa-chevron-left' type='ghost' onClick={() => setCollapsed(true)} />
                </Flexbox>

                <Button class={styles['new-conversation']} type='ghost' onClick={() => context.startNewConversation()}>
                    <i class='fa-regular fa-pen-to-square'></i> New Conversation
                </Button>

                <span style='margin-inline: 20px; margin-top: 16px; margin-bottom: 4px; color: var(--st-panel-fg)'>Conversations</span>

                <Show when={context.conversationsLoading()}>
                    <Loader />
                </Show>

                <For each={context.conversations}>{(conversation) => (
                    <ConversationListItem conversation={conversation} />
                )}</For>
            </Flexbox>
            <div class={styles['conversation-panel']}>
                <div class={styles['conversation-header']}>
                    <Show when={collapsed()}>
                        <Button icon='fa-regular fa-chevron-right' type='ghost' onClick={() => setCollapsed(false)} />
                        <Button icon='fa-regular fa-pen-to-square' type='ghost' onClick={() => context.startNewConversation()} />
                    </Show>
                </div>
                <Conversation />
            </div>
            <div class={styles['references-panel']} data-collapsed={!context.referencesVisible()}>
                <div class={styles['references-header']}>
                    <span>References ({references().length})</span>
                    <Button icon='fa-regular fa-xmark' type='ghost' onClick={() => context.setReferencesVisible(!context.referencesVisible())} />
                </div>

                <References />
            </div>
        </div>
    );
}

function References() {
    const context = requireSuppportAssistAgentContext();
    const traces = createMemo(() => context.messages.flatMap(m => m.traces))
    const references = createMemo(() => traces().flatMap(t => t.orchestration?.knowledgeBaseOutput?.references ?? []))

    createEffect(() => {
        context.setReferencesVisible(references().length > 0);
    })

    return (
        <div class={styles['references-list']}>
            <For each={references()}>{reference => (
                <Reference reference={reference} />
            )}</For>
        </div>
    )
}

function Reference(props: { reference: AgentKnowledgeBaseReference }) {
    const title = createMemo(() => props.reference.documentReference?.filename || props.reference.ticketReference?.ticketNumber|| 'Unknown Reference');
    const [expanded, setExpanded] = createSignal(false);

    const content = createMemo(() => {
        return props.reference.documentReference?.content 
            || props.reference.ticketReference?.content
            || props.reference.content
            || '';
    })

    return (
        <div class={styles['reference']} data-expanded={expanded()} onClick={() => setExpanded(!expanded())}>
            <a class={styles['reference-title']}>{title()}</a>
            <div class={styles['reference-content']}>
                {content()}
            </div>
        </div>
    )
}

function ConversationListItem(props: { conversation: AgentConversation }) {
    const context = requireSuppportAssistAgentContext();

    return (
        <Button class={styles['conversation-list-item']} type="ghost" onClick={() => context.setConversationGu(props.conversation.aiConversationGU)}>
            <span class={styles['conversation-title']} use:tooltip={props.conversation.title}>
                {props.conversation.title}
            </span>
            <Dropdown>
                <Dropdown.Trigger onClick={(e: MouseEvent) => e.stopPropagation()}>
                    <i class='fa-regular fa-ellipsis'></i>
                </Dropdown.Trigger>
                <Dropdown.Content>
                    <ConversationMenu conversation={props.conversation}></ConversationMenu>
                </Dropdown.Content>
            </Dropdown>
        </Button>
    )
}

function ConversationMenu(props: { conversation: AgentConversation}) {
    const context = requireSuppportAssistAgentContext();
    const dd = useDropdownContext();

    return (
        <Flexbox dir='vertical'>
            <Button icon='fa-regular fa-trash-can' type='ghost' aria-label='Delete conversation' onClick={() => { context.deleteConversation(props.conversation.aiConversationGU!); dd?.toggle(false); }}>
                Delete
            </Button>
            <Button icon='fa-regular fa-arrow-up-from-bracket' type='ghost' onClick={() => { context.shareConversation(props.conversation.aiConversationGU!); dd?.toggle(false); }} aria-label='Copy conversation'>Share</Button>
        </Flexbox>
    )

}

function Conversation() {
    const context = requireSuppportAssistAgentContext();
    let messagesContainer: HTMLDivElement | undefined;

    createEffect(() => {
        requestAnimationFrame(() => {
            // Scroll to the bottom of the messages container when messages change
            messagesContainer?.scrollTo({ top: messagesContainer.scrollHeight });
        })
    })

    return (
        <div class={styles['conversation']} data-id={context.conversationGu()}>
            <Show when={context.messages.length} fallback={<NewConversationNote />}>
                <Show when={context.messages} fallback={<div>No conversation found.</div>}>
                    <div class={styles['messages-container']} ref={messagesContainer}>
                        <For each={[...context.messages]}>{(message) => (
                            <Message message={message} />
                        )}</For>
                    </div>
                </Show>
            </Show>

            <ChatInput />
        </div>
    );
}

function NewConversationNote() {
    return (
        <div class={`${styles['new-conversation-note']}`}>
            <span style='font-size: 3em;'>Support Assist</span>
            <div style='font-size: 1.1em; margin-top: 8px;'>
                <span>Type a question or select a conversation to view details.</span>
            </div>
        </div>
    )
}

function Message(props: { message: AgentMessage }) {
    const context = requireSuppportAssistAgentContext();

    return (
        <div class={styles['message']} data-sender={props.message.sender}>
            <div class={styles['message-content']}>
                <Switch>
                    <Match when={props.message.sender === AgentSenderType.Agent}>
                        <AssistantMessage message={props.message} />
                    </Match>
                    <Match when={props.message.sender === AgentSenderType.User}>
                        {props.message.text}
                    </Match>
                </Switch>
            </div>

            <Show when={props.message.sender === AgentSenderType.Agent}>
                <div class={styles['message-actions']}>
                    <Button icon='fa-regular fa-copy' type='ghost' aria-label='Copy message' onClick={() => {
                        navigator.clipboard.writeText(props.message.text || '');
                    }} />

                    <Button
                        class={`${props.message.userRating === UserRating.Positive ? styles['liked'] : ''}`}
                        icon='fa-regular fa-thumbs-up'
                        type='ghost'
                        aria-label='Like message'
                        onClick={() => context.likeMessage(props.message)}
                    />
                    <Button 
                        class={`${props.message.userRating === UserRating.Negative ? styles['disliked'] : ''}`}
                        icon='fa-regular fa-thumbs-down'
                        type='ghost'
                        aria-label='Dislike message'
                        onClick={() => context.dislikeMessage(props.message)}
                    />
                </div>
            </Show>
        </div>
    );
}

function AssistantMessage(props: { message: AgentMessage }) {
    const context = requireSuppportAssistAgentContext();
    return (
        <div>
            <Rationales message={props.message} />
            <Markdown content={props.message.text} />
        </div>
    )
}

function Rationales(props: { message: AgentMessage }) {
    const rationales = createMemo(() => props.message.traces.map(t => t.orchestration?.rationale?.text ?? '').filter(r => r.length > 0));
    const [expanded, setExpanded] = createSignal(true);
    const loading = createMemo(() => props.message.text === undefined || props.message.text.length === 0);

    const message = createMemo(() => {
        if (!props.message.text) return 'Thinking...';
        return 'Thought for a few seconds...';
    })

    createEffect(() => {
        if (!loading()) {
            setExpanded(false);
        } else {
            setExpanded(true);
        }
    })

    return (
        <div class={styles['rationales-container']} data-expanded={expanded()}>
            <div class={`${styles['rationale']}`} classList={{ [styles['shine']]: loading(), }} onClick={() => setExpanded(!expanded())}>
                <span style='padding-right: 6px'>{message()}</span>
                <i class='fa fa-chevron-down' style={{
                    cursor: 'pointer',
                    "font-size": '0.9em',
                    transition: 'transform 0.2s ease-in-out',
                    transform: expanded() ? 'rotate(0deg)' : 'rotate(-90deg)',
                }}  />
            </div>

            <div class={styles['rationale-timeline']}>
                <For each={rationales().slice(0, -1) ?? []}>{(rationale) => (
                    <div>
                        <span class={styles['rationale-timeline-item']}>{rationale}</span>
                        <div class={styles['rationale-timeline-bar']}></div>
                    </div>
                )}</For>
                <Show when={!loading() && rationales().length}>
                    <div>
                        <span class={`${styles['rationale-timeline-item']}`}>{rationales().at(-1)}</span>
                    </div>
                </Show>
            </div>
            <Show when={loading()}>
                <div class={styles['rationale-timeline']}>
                    <span class={`${styles['rationale-timeline-item']} ${styles['shine']}`}>{rationales().at(-1) || 'Submitting query to Synergy AI'}</span>
                </div>
            </Show>
        </div>
    )
}

function ChatInput() {
    const context = requireSuppportAssistAgentContext();
    let textarea: HTMLTextAreaElement | undefined;

    return (
        <div class={styles['chat-input-container']} onClick={() => textarea?.focus()}>
            <textarea
                value={context.message()}
                rows="1"
                ref={textarea}
                class={styles['chat-input']}
                placeholder='Ask anything...'
                style='height: 1.3em'
                onInput={(e) => {
                    e.target.style.height = '5px'; // Reset height
                    e.target.style.height = `${e.target.scrollHeight}px`; // Set to scroll height
                    context.setMessage(e.target.value);
                }}
            />
            <Button icon='fa-solid fa-paper-plane' type='ghost' aria-label='Send' onClick={() => context.sendMessage(context.message())} />
        </div>
    )
}

SuppportAssistAgent.defaultProps = new SuppportAssistAgentProps();